import { FileInfoDto } from '@lmt/shared-types';
import { AppTooltip } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppTooltip';
import {
  aggregateTransportStatus,
  getDocumentTitle,
  useFileCallbacks,
} from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/common';
import { DocumentIcon } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/DocumentIcon';
import { DocumentLoader } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/DocumentLoader';
import { DocumentOptionButton } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/DocumentOptionButton';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useMemo } from 'react';
import { DocumentTransportTooltip } from './DocumentTransportTooltip';
import styles from '@lmt/unicorn-shared/src/app/components/patients/profile/cases/components/documents/DocumentItem.module.scss';

interface Props {
  document: FileInfoDto;
  isReadOnly?: boolean;
}

export const DocumentItem: FC<Props> = (props) => {
  const i18next = useScopedTranslation('patients.documents');
  const { document: patientDocument, isReadOnly } = props;
  const isOutdated = !!patientDocument.values?.['IsOutdated'];
  const { isLoading, onClick } = useFileCallbacks(patientDocument);
  const title = useMemo(
    () => getDocumentTitle(patientDocument.name),
    [patientDocument.name],
  );

  const hasTransportInfo =
    !!patientDocument.transportInfo &&
    (patientDocument.transportInfo.hL7?.status != null ||
      patientDocument.transportInfo.gdt?.status != null ||
      patientDocument.transportInfo.email?.status != null);

  return (
    <div className={styles.container} data-test-id="document-item">
      <div className={styles.titleContainer} onClick={onClick}>
        {isLoading && <DocumentLoader />}
        {hasTransportInfo ? (
          <AppTooltip
            title={<DocumentTransportTooltip file={patientDocument} />}
            classname={styles.abab}
          >
            <div>
              <DocumentIcon
                data-loading={isLoading}
                fileType={patientDocument.type}
                filename={patientDocument.name}
                className={styles.icon}
                transportStatus={aggregateTransportStatus(patientDocument)}
              />
            </div>
          </AppTooltip>
        ) : (
          <div>
            <DocumentIcon
              data-loading={isLoading}
              fileType={patientDocument.type}
              filename={patientDocument.name}
              className={styles.icon}
            />
          </div>
        )}

        <div>
          <div
            title={patientDocument.name}
            className={styles.title}
            data-outdated={isOutdated}
          >
            {title}
          </div>
          {isOutdated && (
            <div
              className={styles.outdatedLabel}
              title={i18next.t('outdated_document_tooltip')}
            >
              {i18next.t('outdated_document')}
            </div>
          )}
        </div>
      </div>
      {!isReadOnly && (
        <div className={styles.optionContainer}>
          <div className={styles.option}>
            <DocumentOptionButton document={patientDocument} />
          </div>
        </div>
      )}
    </div>
  );
};
